import { Injectable } from '@angular/core';
import { SignalCoordinatorService } from './signal-coordinator.service';
import { PermissionService } from '../permissions/permission.service';
import { PermissionSyncService } from '../permissions/permission-sync.service';
import { DrawingCompletionNotifier } from '../drawings/drawing-completion-notifier.service';
import { SignalProvider } from './signal-provider.interface';
import { PermissionRouteWatcher } from '../permissions/permission-route-watcher.service';
import { FirebaseSessionService } from './firebase-session.service';

/**
 * One place to start and stop everything that is tied to a logged-in session:
 * the Signal Bus (widget refresh) and permission sync.
 *
 * These must always move together. SignalCoordinatorService.teardown() calls
 * signalProvider.disconnectAll(), which also drops the permission listener — so
 * tearing one down without the other leaves permission sync silently dead while
 * still appearing to be running.
 *
 * Call sites: login, logout, 401 sign-out, office switch, end impersonation.
 *
 * Every listener waits for Firebase sign-in (FirebaseSessionService): the security rules
 * deny anything not signed in with an ERP-issued token.
 */
@Injectable({ providedIn: 'root' })
export class RealtimeSessionService {
  private startedFamilyRootId: number | null = null;

  /**
   * Bumped on every start/stop. Sign-in is async, so a stop (logout, office switch) can land
   * while it is in flight — the stale start must then not connect anything.
   */
  private generation = 0;

  constructor(
    private signalCoordinator: SignalCoordinatorService,
    private permissions: PermissionService,
    private permissionSync: PermissionSyncService,
    private permissionRouteWatcher: PermissionRouteWatcher,
    private drawingNotifier: DrawingCompletionNotifier,
    private signalProvider: SignalProvider,
    private firebaseSession: FirebaseSessionService
  ) {}

  /** Start (or restart, e.g. after an office switch) for the given session. */
  start(
    familyRootId: number | null | undefined,
    userId: number | null | undefined,
    signalNamespace?: string | null
  ): void {
    const rootId = Number(familyRootId);
    const uid = Number(userId);
    if (!rootId || !uid) {
      return;
    }

    if (this.startedFamilyRootId !== null && this.startedFamilyRootId !== rootId) {
      this.stop({ clearPermissions: false }); // office switch — same user, new family
    }

    // The SERVER decides which environment namespace to listen on — every environment shares
    // one Firebase project and RTDB instance, so this is what keeps dev, UAT and production
    // apart. Applied before anything connects, or listeners bind to the wrong path.
    this.signalProvider.setNamespace(signalNamespace);

    this.startedFamilyRootId = rootId;
    const generation = ++this.generation;

    // A fresh token every start: its family claim is what the rules check, so it has to
    // follow office switches and impersonation. Connect even if sign-in failed — the app
    // must not depend on Firebase, and a failed listener just means no live updates.
    this.firebaseSession.signIn().then(() => {
      if (generation !== this.generation) {
        return; // stopped or restarted while signing in
      }
      this.signalCoordinator.initialize(rootId);
      this.permissionSync.start(rootId, uid);
      this.permissionRouteWatcher.start();
      // Watches the drawings feed app-wide so a completion is announced wherever the user is.
      // uid is already resolved above for permissionSync.start() — reused here so the
      // completion toast can be shown only to the user who uploaded the file.
      this.drawingNotifier.start(rootId, uid);
    });
  }

  /**
   * Resume an existing session on app bootstrap (reload while logged in).
   *
   * Prefers the cached auth_detail; if it is missing or has no family root —
   * it is only written by the header component, so a freshly reloaded tab may
   * not have it — falls back to asking the API, whose payload carries both ids.
   */
  resume(): void {
    let authDetail: any = null;
    try {
      authDetail = JSON.parse(localStorage.getItem('auth_detail') || 'null');
    } catch {
      authDetail = null; // malformed — fall through to the API probe
    }

    if (authDetail?.family_root_id && authDetail?.id) {
      this.start(authDetail.family_root_id, authDetail.id, authDetail.signal_namespace);
      return;
    }

    this.permissionSync.probe().subscribe({
      next: (response) => {
        const payload = response?.data;
        if (!payload) {
          return;
        }
        this.permissions.apply(payload);
        this.start(payload.family_root_id, payload.user_id, payload.signal_namespace);
      },
      error: () => {
        // Not logged in, or the API is unreachable. The 401 interceptor handles
        // an expired session; there is nothing to resume here.
      },
    });
  }

  /**
   * Stop everything. `clearPermissions` should be true when the session itself
   * ends (logout / 401) and false for an office switch, where the same user
   * stays signed in and clearing would blank the UI mid-navigation.
   */
  stop(options: { clearPermissions?: boolean } = {}): void {
    const { clearPermissions = true } = options;
    this.generation++;

    this.permissionRouteWatcher.stop();
    this.permissionSync.stop();
    // Clears its notified/seeded state too, so an office switch re-baselines instead of
    // announcing the new office's already-finished drawings.
    this.drawingNotifier.stop();
    this.signalCoordinator.teardown();
    this.startedFamilyRootId = null;

    if (clearPermissions) {
      this.permissions.clear();
      // Session over — drop the Firebase session with it. An office switch keeps it; the
      // following start() replaces it with a token for the new family.
      this.firebaseSession.signOut();
    }
  }
}
